import { useEffect, useMemo, Fragment } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery } from '@mui/material';

// third-party
import {
  useFilters,
  useExpanded,
  useGlobalFilter,
  useRowSelect,
  useSortBy,
  useTable,
  usePagination,
  Column,
  HeaderGroup,
  Row,
  Cell
} from 'react-table';

import { EmptyTable, TablePagination } from 'components/third-party/ReactTable';

import { renderFilterTypes, GlobalFilter } from 'utils/react-table';

// assets
import { Add } from 'iconsax-react';
import { IFund, ISector } from 'types/custom/common';
import { useFirebaseContext } from 'contexts/FirebaseContext';
type Type = 'sector' | 'fund' | null;
// ==============================|| REACT TABLE ||============================== //

interface Props {
  columns: Column[];
  data: ISector[] | IFund[];
  setIsSave: (val: boolean) => void;
  setType: (val: Type) => void;
  emptyMessage: string;
  type: Type;
}

export function ConfigsTable({ columns, data, emptyMessage, type, setIsSave, setType }: Props) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

  const filterTypes = useMemo(() => renderFilterTypes, []);
  const { user } = useFirebaseContext();
  const permissions = user?.permissions;
  const hasEditPermission = permissions?.includes('canUpdateConfig');

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    setHiddenColumns,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: { pageIndex: 0, pageSize: 10, hiddenColumns: ['avatar', 'email'] }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  useEffect(() => {
    if (matchDownSM) {
      setHiddenColumns(['age', 'contact', 'visits', 'email', 'status', 'avatar']);
    } else {
      setHiddenColumns(['avatar', 'email']);
    }
    // eslint-disable-next-line
  }, [matchDownSM]);

  return (
    <>
      <Stack spacing={3}>
        <Stack
          direction={matchDownSM ? 'column' : 'row'}
          spacing={1}
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 3, pb: 0 }}
        >
          <GlobalFilter
            sx={{ width: 500 }}
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
          <Stack direction={matchDownSM ? 'column' : 'row'} alignItems="center" spacing={2}>
            {hasEditPermission && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setType(type);
                  setIsSave(true);
                }}
                size="small"
              >
                {type === 'sector' ? 'Create Sector' : 'Create Fund'}
              </Button>
            )}
          </Stack>
        </Stack>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup: HeaderGroup<{}>) => {
              const { key, ...restProps } = headerGroup.getHeaderGroupProps();
              return (
                <TableRow {...restProps} key={key} sx={{ '& > th:first-of-type': { width: '58px' } }}>
                  {headerGroup?.headers.map((column: HeaderGroup) => {
                    const { key, ...restProps } = column.getHeaderProps([{ className: column.className }]);
                    return (
                      <TableCell sx={{ p: 1.5 }} {...restProps} key={key}>
                        {column.render('Header')}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableHead>
          {page.length === 0 ? (
            <TableBody>
              <EmptyTable msg={emptyMessage} colSpan={10} />
            </TableBody>
          ) : (
            <TableBody {...getTableBodyProps()}>
              {page.map((row: Row, i: number) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                const { key, ...restProps } = rowProps;

                return (
                  <Fragment key={i}>
                    <TableRow
                      key={key}
                      {...restProps}
                      onClick={() => {
                        row.toggleRowExpanded();
                      }}
                      sx={{ cursor: 'pointer', bgcolor: row.isSelected ? alpha(theme.palette.primary.lighter, 0.35) : 'inherit' }}
                    >
                      {row.cells.map((cell: Cell) => {
                        const { key, ...restProps } = cell.getCellProps([{ className: cell.column.className }]);
                        return (
                          <TableCell sx={{ p: 0.9 }} {...restProps} key={key}>
                            {cell.render('Cell')}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </Fragment>
                );
              })}
              <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
                <TableCell sx={{ p: 1, py: 2 }} colSpan={9}>
                  <TablePagination gotoPage={gotoPage} rows={rows} setPageSize={setPageSize} pageSize={pageSize} pageIndex={pageIndex} />
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </Stack>
    </>
  );
}
